import { Fragment, useCallback, useEffect, useState } from "react";
import { Download, Send, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { money, fmtDate, fmtDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export async function downloadStatement(s) {
  const { data } = await api.get(`/statements/${s.id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${s.loan_number}-statement-${s.period_end}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const Deliveries = ({ items }) => (
  <div className="space-y-1.5 bg-muted/30 px-6 py-3" data-testid="delivery-log">
    {items.length ? items.map((d, i) => (
      <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" data-testid={`delivery-entry-${i}`}>
        <StatusBadge status={d.status} />
        <span className="num">{fmtDateTime(d.at)} ET</span>
        <span>to <b>{d.recipient}</b></span>
        <span className="text-muted-foreground">from {d.sender} · {d.trigger}</span>
        {d.error && <span className="text-destructive" data-testid={`delivery-error-${i}`}>{d.error}</span>}
      </div>
    )) : <div className="text-xs text-muted-foreground">Not emailed yet.</div>}
  </div>
);

export const StatementsPanel = ({ loanId, readOnly, refreshKey }) => {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get(`/loans/${loanId}/statements`).then((r) => setItems(r.data.items)), [loanId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const act = async (s, kind) => {
    setBusy(`${kind}-${s.id}`);
    try {
      if (kind === "dl") await downloadStatement(s);
      else {
        const { data } = await api.post(`/statements/${s.id}/send`);
        data.status === "Sent" ? toast.success(`Statement emailed to ${data.recipient}`) : toast.error(`Email failed: ${data.error}`);
        setOpen((o) => ({ ...o, [s.id]: true }));
        load();
      }
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setBusy("");
    }
  };

  if (!items) return <div className="px-6 py-10 text-sm text-muted-foreground">Loading statements…</div>;
  if (!items.length) return <div className="px-6 py-12 text-center text-sm text-muted-foreground" data-testid="statements-empty">No statements yet.</div>;
  const cols = ["Statement date", "Period", "Closing balance", "Amount due", ...(readOnly ? [] : ["Email"]), ""];
  return (
    <div className="overflow-x-auto">
      <Table data-testid="statements-table">
        <TableHeader><TableRow className="hover:bg-transparent">{cols.map((c, i) => <TableHead key={i} className="eyebrow h-11 whitespace-nowrap">{c}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {items.map((s) => (
            <Fragment key={s.id}>
              <TableRow data-testid={`statement-row-${s.period_end}`}>
                <TableCell className="whitespace-nowrap font-medium">{fmtDate(s.period_end)}{s.is_final && <span className="ml-2 text-xs text-muted-foreground">Final</span>}</TableCell>
                <TableCell className="text-sm">{fmtDate(s.period_start)} – {fmtDate(s.period_end)}</TableCell>
                <TableCell className="num">{money(s.closing_principal_cents)}</TableCell>
                <TableCell className="num font-semibold">{money(s.amount_due_cents)}</TableCell>
                {!readOnly && <TableCell><StatusBadge status={s.email_status} testid={`statement-email-status-${s.period_end}`} /></TableCell>}
                <TableCell className="text-right">
                  <div className="flex flex-wrap justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => act(s, "dl")} disabled={!!busy} data-testid={`download-statement-${s.period_end}`}>
                    {busy === `dl-${s.id}` ? <Loader2 className="animate-spin" /> : <Download />} Download PDF
                  </Button>
                  {!readOnly && <>
                    <Button variant="ghost" size="sm" onClick={() => act(s, "send")} disabled={!!busy} data-testid={`send-statement-${s.period_end}`}>
                      {busy === `send-${s.id}` ? <Loader2 className="animate-spin" /> : <Send />} {s.email_status === "unsent" ? "Send" : "Resend"}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setOpen((o) => ({ ...o, [s.id]: !o[s.id] }))} data-testid={`toggle-delivery-log-${s.period_end}`}>
                      Log ({s.deliveries.length}) <ChevronDown className={open[s.id] ? "rotate-180" : ""} />
                    </Button>
                  </>}
                  </div>
                </TableCell>
              </TableRow>
              {!readOnly && open[s.id] && <TableRow className="hover:bg-transparent"><TableCell colSpan={6} className="max-w-0 p-0"><Deliveries items={s.deliveries} /></TableCell></TableRow>}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
